import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LinearProgress from '@mui/material/LinearProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

// 24 sep 2026 (Augusto: "PCM debe ayudar a reconciliar las cuentas,
// asistir al usuario a que cuadren las cuentas. Y dejar el aplicativo
// listo para seguir ingresando data").
//
// Conciliar es responder una pregunta: a esta fecha, ¿PCM dice lo mismo
// que el banco? Pero la respuesta útil no es la diferencia sino QUÉ la
// explica. Por eso esta pantalla no muestra un número y se calla: cuando
// no cuadra, lista las pistas que el backend ya calculó --movimientos en
// cuarentena, meses en blanco, cuentas sin actividad-- cada una con el
// botón que lleva a resolverla.
//
// "Conciliación" y no "reconciliación": en contabilidad el término es
// conciliación bancaria. Reconciliarse es entre personas.

const simboloDe = (moneda) => ((moneda || 'PEN').toUpperCase() === 'USD' ? '$' : 'S/');
const plata = (n, moneda) => `${simboloDe(moneda)} ${Number(n || 0).toFixed(2)}`;
const hoyISO = () => new Date().toISOString().slice(0, 10);

const ConciliacionPage = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const navigate = useNavigate();

  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  // Cuenta que se está conciliando ahora mismo, y lo que se escribió.
  const [abierta, setAbierta] = useState(null); // account_id
  const [fecha, setFecha] = useState(hoyISO());
  const [saldoBanco, setSaldoBanco] = useState('');
  const [resultado, setResultado] = useState(null);
  const [trabajando, setTrabajando] = useState(false);
  // 24 sep 2026 (Augusto: "la idea es que se utilice balance final, que
  // se convierte en el balance inicial del siguiente mes, y el balance
  // corriente"). La cadena mes a mes de la cuenta abierta.
  const [meses, setMeses] = useState(null);
  // 25 sep 2026 (Augusto: "que se metan los estados de tarjetas y
  // cuentas y que PCM ayude a conciliar para que el usuario empiece a
  // meter datos diarios"). El camino completo, en orden.
  const [puesta, setPuesta] = useState(null);
  // 25 sep 2026 (Augusto: "¿puede PCM reconciliar con la información de
  // los estados ya importados?"). Sí: cada extracto dejó guardado el
  // saldo que declaraba el banco. Es lo mismo que uno copiaría a mano,
  // ya escrito por el banco, y para todos los períodos de una.
  const [desdeExtractos, setDesdeExtractos] = useState(null);
  const [conciliandoAuto, setConciliandoAuto] = useState(false);
  // 25 sep 2026 (Augusto: "Conciliación no asiste al usuario a
  // reconciliar. Se necesita un apoyo de IA"). El código ya calcula la
  // diferencia y busca coincidencias exactas; esto le pide a la IA que
  // lo explique en palabras y diga qué hacer.
  const [ayuda, setAyuda] = useState(null);
  const [pidiendoAyuda, setPidiendoAyuda] = useState(false);
  // 25 sep 2026: el saldo de hoy dicho por el usuario, y el historial de
  // importaciones ("para saber qué se tiene").
  const [saldoHoy, setSaldoHoy] = useState({}); // { [accountId]: valor }
  const [importaciones, setImportaciones] = useState(null);
  const [verImportaciones, setVerImportaciones] = useState(false);
  // 25 sep 2026 (Augusto: "eliminar registro de importaciones que fueron
  // eliminadas o duplicadas"). Borra SOLO la línea del historial: los
  // movimientos no se tocan (ver borrar_registro_de_importacion).
  const [borrandoImp, setBorrandoImp] = useState(null);
  // 28 sep 2026 (Augusto: "al momento no tengo idea qué importé"). El
  // nombre del archivo nunca llegaba al servidor (ya corregido para los
  // imports nuevos); los que ya están no se pueden recuperar, así que se
  // deja que él los etiquete. { id: texto }
  const [editandoNombre, setEditandoNombre] = useState(null);
  const [nombreEditado, setNombreEditado] = useState('');
  const sinAsociar = (importaciones || []).filter(
    (i) => !i.account_id && !i.reconstruido
  ).length;
  const recargarImportaciones = () =>
    apiClient.get('/conciliacion/importaciones')
      .then((r) => setImportaciones(r.data || []))
      .catch(() => {});
  const guardarNombre = async (imp) => {
    const texto = nombreEditado.trim();
    setEditandoNombre(null);
    if (texto === (imp.archivo || '')) return;
    try {
      await apiClient.patch(`/conciliacion/importaciones/${imp.id}`, { archivo: texto });
      await recargarImportaciones();
    } catch {
      // Un "reconstruido" no tiene registro propio donde guardar el
      // nombre; el backend lo explica y acá simplemente no cambia nada.
    }
  };
  const borrarImportacion = async (imp) => {
    setBorrandoImp(imp.id);
    try {
      await apiClient.delete(`/conciliacion/importaciones/${imp.id}`);
      await recargarImportaciones();
    } finally {
      setBorrandoImp(null);
    }
  };
  const limpiarSinAsociar = async () => {
    setBorrandoImp('todas');
    try {
      await apiClient.delete('/conciliacion/importaciones');
      await recargarImportaciones();
    } finally {
      setBorrandoImp(null);
    }
  };
  const [anio, setAnio] = useState(new Date().getFullYear());

  const cargar = useCallback(() => {
    setCargando(true);
    apiClient.get('/conciliacion/importaciones')
      .then((res) => setImportaciones(res.data))
      .catch(() => setImportaciones(null));
    apiClient.get('/conciliacion/desde-extractos')
      .then((res) => setDesdeExtractos(res.data))
      .catch(() => setDesdeExtractos(null));
    apiClient.get('/conciliacion/puesta-a-punto')
      .then((res) => setPuesta(res.data))
      .catch(() => setPuesta(null));
    apiClient.get('/conciliacion/estado')
      .then((res) => { setEstado(res.data); setError(null); })
      .catch(() => setError(isSpanish ? 'No se pudo cargar el estado de tus cuentas.' : 'Could not load your accounts.'))
      .finally(() => setCargando(false));
  }, [isSpanish]);

  useEffect(cargar, [cargar]);

  const cargarMeses = useCallback((accountId, anioPedido) => {
    apiClient.get(`/conciliacion/meses/${accountId}`, { params: { anio: anioPedido } })
      .then((res) => setMeses(res.data))
      .catch(() => setMeses(null));
  }, []);

  const fijarSaldoHoy = async (cuenta) => {
    const valor = saldoHoy[cuenta.account_id];
    if (valor === undefined || valor === '') return;
    setTrabajando(true);
    setError(null);
    try {
      await apiClient.post('/conciliacion/saldo-hoy', {
        account_id: cuenta.account_id,
        saldo: Number(valor),
      });
      setSaldoHoy((p) => ({ ...p, [cuenta.account_id]: '' }));
      cargar();
    } catch (err) {
      setError(err.response?.data?.detail || (isSpanish ? 'No se pudo guardar el saldo.' : 'Could not save the balance.'));
    } finally {
      setTrabajando(false);
    }
  };

  const pedirAyuda = async (cuenta) => {
    setPidiendoAyuda(true);
    setError(null);
    try {
      const { data } = await apiClient.post('/conciliacion/asistente', {
        account_id: cuenta.account_id,
        fecha,
        saldo_banco: Number(saldoBanco),
      });
      setAyuda(data);
    } catch (err) {
      setError(err.response?.data?.detail || (isSpanish ? 'No se pudo consultar al asistente.' : 'Could not reach the assistant.'));
    } finally {
      setPidiendoAyuda(false);
    }
  };

  const conciliarDesdeExtractos = async () => {
    setConciliandoAuto(true);
    setError(null);
    try {
      await apiClient.post('/conciliacion/desde-extractos');
      cargar();
    } catch (err) {
      setError(err.response?.data?.detail || (isSpanish ? 'No se pudo conciliar.' : 'Could not reconcile.'));
    } finally {
      setConciliandoAuto(false);
    }
  };

  const abrir = (cuenta) => {
    setAbierta(cuenta.account_id);
    setFecha(hoyISO());
    setSaldoBanco('');
    setResultado(null);
    setError(null);
    setMeses(null);
    cargarMeses(cuenta.account_id, anio);
  };

  const comparar = async (cuenta) => {
    if (saldoBanco === '') return;
    setTrabajando(true);
    setError(null);
    try {
      const { data } = await apiClient.post('/conciliacion/preview', {
        account_id: cuenta.account_id,
        fecha,
        saldo_banco: Number(saldoBanco),
      });
      setResultado(data);
      setAyuda(null);
    } catch (err) {
      setError(err.response?.data?.detail || (isSpanish ? 'No se pudo comparar.' : 'Could not compare.'));
    } finally {
      setTrabajando(false);
    }
  };

  const confirmar = async (cuenta, aceptarSaldoBanco) => {
    setTrabajando(true);
    setError(null);
    try {
      const { data } = await apiClient.post('/conciliacion/confirmar', {
        account_id: cuenta.account_id,
        fecha,
        saldo_banco: Number(saldoBanco),
        aceptar_saldo_banco: aceptarSaldoBanco,
      });
      setResultado(data);
      cargar();
      cargarMeses(cuenta.account_id, anio);
    } catch (err) {
      setError(err.response?.data?.detail || (isSpanish ? 'No se pudo guardar.' : 'Could not save.'));
    } finally {
      setTrabajando(false);
    }
  };

  const textoPista = (p, moneda) => {
    if (p.tipo === 'pendientes_revision') {
      return isSpanish
        ? `${p.cantidad} movimiento(s) esperando una decisión en Revisión pendiente. Suman ${plata(p.impacto, moneda)} que hoy NO se están contando — es la causa más frecuente.`
        : `${p.cantidad} transaction(s) awaiting a decision under Pending review. They add up to ${plata(p.impacto, moneda)} that is not being counted — the most common cause.`;
    }
    if (p.tipo === 'meses_sin_movimientos') {
      return isSpanish
        ? `No hay ni un movimiento registrado en: ${p.meses.join(', ')}. Un mes entero en blanco casi siempre es un estado de cuenta que falta importar.`
        : `No transactions at all in: ${p.meses.join(', ')}. A whole empty month is almost always a statement you haven't imported.`;
    }
    if (p.tipo === 'sin_movimientos_recientes') {
      return isSpanish
        ? `El último movimiento de esta cuenta es del ${p.ultimo} — hace ${p.dias} días. Si la cuenta se usa, falta todo ese período.`
        : `The last transaction here is from ${p.ultimo} — ${p.dias} days ago. If the account is in use, that whole period is missing.`;
    }
    return null;
  };

  const etiquetaPista = (p) => {
    if (p.tipo === 'pendientes_revision') return isSpanish ? 'Ir a Revisión' : 'Go to Review';
    return isSpanish ? 'Ver transacciones' : 'View transactions';
  };

  if (cargando && !estado) {
    return (
      <Container maxWidth="md" sx={{ pt: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const cuentas = estado?.cuentas || [];

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isSpanish ? 'Conciliación' : 'Reconciliation'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isSpanish
          ? 'Compara lo que dice tu banco con lo que tiene PCM. Cuando no coincide, acá te digo qué lo explica y dónde arreglarlo.'
          : "Compare what your bank says with what PCM has. When they differ, here's what explains it and where to fix it."}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 25 sep 2026: el camino de "acabo de entrar" a "ya puedo
          registrar día a día". El orden no es decorativo: registrar
          gastos sobre saldos que no cuadran es construir sobre arena --
          el tablero va a estar mal igual, y el usuario deja de confiar
          en él justo cuando empezaba a usarlo. */}
      {puesta && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {isSpanish ? 'Puesta a punto' : 'Getting set up'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {puesta.completados}/{puesta.total}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(puesta.completados / puesta.total) * 100}
            sx={{ mb: 2, height: 6, borderRadius: 3 }}
          />

          {puesta.listo_para_el_dia_a_dia && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {isSpanish
                ? 'Tus cuentas cuadran y no hay nada pendiente: ya puedes registrar tus gastos del día a día y confiar en los números del tablero.'
                : 'Your accounts match and nothing is pending: you can start recording day-to-day expenses and trust the dashboard numbers.'}
            </Alert>
          )}

          {puesta.pasos.map((p) => (
            <Box key={p.clave} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}>
              {p.listo
                ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', mt: '2px' }} />
                : <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled', mt: '2px' }} />}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: p.listo ? 400 : 600, color: p.listo ? 'text.secondary' : 'text.primary' }}
                >
                  {p.titulo}
                </Typography>
                <Typography variant="caption" color="text.secondary">{p.detalle}</Typography>
              </Box>
              {!p.listo && (
                <Button size="small" onClick={() => navigate(p.ruta)}>
                  {isSpanish ? 'Ir' : 'Go'}
                </Button>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {/* 25 sep 2026: no tiene sentido pedirle a mano un saldo que el
          banco ya escribió y PCM ya guardó al importar el extracto. */}
      {desdeExtractos?.por_conciliar > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={conciliandoAuto}
              onClick={conciliarDesdeExtractos}
            >
              {isSpanish ? 'Conciliar con mis extractos' : 'Reconcile from my statements'}
            </Button>
          }
        >
          {isSpanish
            ? `Tengo ${desdeExtractos.por_conciliar} corte(s) con el saldo que declaró tu banco, de los estados de cuenta que ya importaste. Puedo conciliarlos ahora sin que escribas nada: ${desdeExtractos.cuadran} cuadran y ${desdeExtractos.con_diferencia} tienen diferencia.`
            : `I have ${desdeExtractos.por_conciliar} statement balance(s) from the statements you already imported. I can reconcile them now without you typing anything: ${desdeExtractos.cuadran} match and ${desdeExtractos.con_diferencia} differ.`}
        </Alert>
      )}

      {/* 25 sep 2026 (Augusto: "sería bueno que en algún lugar de PCM se
          muestre fecha y hora cuando se importaron los estados bancarios
          y de tarjetas. Para saber qué se tiene"). Sin esto, la única
          forma de saber si agosto del BCP ya estaba era ponerse a mirar
          transacciones de ese mes y deducirlo -- y deducir mal lleva a
          importar dos veces o a creer que falta algo que ya está. */}
      {importaciones && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {isSpanish ? `Extractos importados (${importaciones.length})` : `Imported statements (${importaciones.length})`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* 25 sep 2026 (Augusto: "eliminar registro de
                  importaciones que fueron eliminadas o duplicadas"). Las
                  "Sin asociar" son importaciones cuya cuenta se borró
                  después: sus movimientos se fueron con la cuenta, así que
                  la línea anuncia movimientos que ya no existen. */}
              {sinAsociar > 0 && (
                <Button size="small" color="error" onClick={limpiarSinAsociar} disabled={borrandoImp === 'todas'}>
                  {isSpanish
                    ? `Limpiar sin asociar (${sinAsociar})`
                    : `Clear unlinked (${sinAsociar})`}
                </Button>
              )}
              {importaciones.length > 0 && (
                <Button size="small" onClick={() => setVerImportaciones((v) => !v)}>
                  {verImportaciones
                    ? (isSpanish ? 'Ocultar' : 'Hide')
                    : (isSpanish ? 'Ver' : 'Show')}
                </Button>
              )}
            </Box>
          </Box>
          {/* 25 sep 2026 (Augusto: "no veo dónde aparece la lista de
              estados bancarios importados"). El panel se muestra siempre,
              aunque esté vacío: si solo apareciera cuando hay datos, la
              primera pregunta de cualquiera sería justamente dónde está.
              Y hace falta decir por qué está vacío -- el registro empieza
              hoy, las importaciones viejas no se pueden reconstruir. */}
          {importaciones.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {isSpanish
                ? 'Todavía no hay ninguno. Cada estado de cuenta que importes queda acá con su fecha y hora, la cuenta y el período que cubre.'
                : 'Nothing yet. Every statement you import is listed here with its date and time, the account and the period it covers.'}
            </Typography>
          )}
          {verImportaciones && (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>{isSpanish ? 'Importado' : 'Imported'}</TableCell>
                    <TableCell>{isSpanish ? 'Cuenta' : 'Account'}</TableCell>
                    <TableCell>{isSpanish ? 'Archivo' : 'File'}</TableCell>
                    <TableCell>{isSpanish ? 'Período' : 'Period'}</TableCell>
                    <TableCell align="right">{isSpanish ? 'Movim.' : 'Txns'}</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importaciones.map((imp) => (
                    <TableRow key={imp.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {imp.cuando ? new Date(imp.cuando).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        {imp.cuenta || (
                          <Typography variant="caption" color="text.secondary">
                            {isSpanish ? 'Sin asociar' : 'Unlinked'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        onDoubleClick={() => {
                          if (imp.reconstruido) return;
                          setEditandoNombre(imp.id);
                          setNombreEditado(imp.archivo || '');
                        }}
                        sx={{ cursor: imp.reconstruido ? 'default' : 'text', minWidth: 170 }}
                      >
                        {editandoNombre === imp.id ? (
                          <TextField
                            autoFocus
                            size="small"
                            variant="standard"
                            placeholder={isSpanish ? 'Amex agosto' : 'Amex August'}
                            value={nombreEditado}
                            onChange={(e) => setNombreEditado(e.target.value)}
                            onBlur={() => guardarNombre(imp)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') guardarNombre(imp);
                              if (e.key === 'Escape') setEditandoNombre(null);
                            }}
                          />
                        ) : (
                          imp.archivo || (
                            <Typography variant="caption" color="text.secondary">
                              {imp.reconstruido
                                ? (isSpanish ? 'reconstruido' : 'reconstructed')
                                : (isSpanish ? 'sin nombre — doble clic' : 'no name — double-click')}
                            </Typography>
                          )
                        )}
                      </TableCell>
                      <TableCell>{imp.periodo || '—'}</TableCell>
                      <TableCell align="right">
                        {imp.movimientos ?? '—'}
                        {imp.pendientes > 0 && (
                          <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
                            ({imp.pendientes} {isSpanish ? 'pend.' : 'pend.'})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            imp.reconstruido
                              ? (isSpanish
                                  ? 'Quitar del historial. Esta fila se dedujo del saldo declarado del extracto, así que borrarla también quita ese saldo de la conciliación de ese período.'
                                  : 'Remove from the log. This row was derived from the statement balance, so deleting it also removes that balance from the reconciliation of that period.')
                              : (isSpanish
                                  ? 'Quitar del historial. No borra ningún movimiento.'
                                  : 'Remove from the log. Does not delete any transaction.')
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={borrandoImp === imp.id}
                              onClick={() => borrarImportacion(imp)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {isSpanish
                  ? 'Las filas marcadas "reconstruido" son importaciones anteriores a este registro: se recuperaron del saldo que declaraba cada extracto, así que tienen fecha, cuenta y período, pero no el archivo ni cuántos movimientos trajeron — eso nunca se guardó.'
                  : 'Rows marked "reconstructed" are imports made before this log existed: they were recovered from the balance each statement declared, so they have a date, account and period, but no filename or transaction count — that was never stored.'}
              </Typography>
            </TableContainer>
          )}
        </Paper>
      )}

      {cuentas.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {isSpanish
              ? 'Todavía no tienes cuentas de ahorros o corrientes. Agrégalas en Cuentas y vuelve acá.'
              : 'You have no savings or checking accounts yet. Add them under Accounts and come back.'}
          </Typography>
        </Paper>
      )}

      {cuentas.map((cuenta) => {
        const estaAbierta = abierta === cuenta.account_id;
        const ultima = cuenta.ultima_conciliacion;
        return (
          <Paper key={cuenta.account_id} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {cuenta.nombre}
                  {cuenta.entidad ? (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      {cuenta.entidad}
                    </Typography>
                  ) : null}
                </Typography>
                {/* 25 sep 2026 (Augusto: "sería mejor [fechar el
                    saldo]"). Un número sin fecha se lee como si fuera de
                    hoy, y casi nunca lo es: si el último extracto es de
                    julio, ese saldo es de julio. */}
                <Typography variant="body2" color="text.secondary">
                  {cuenta.saldo_al
                    ? (isSpanish ? `Saldo al ${cuenta.saldo_al}: ` : `Balance as of ${cuenta.saldo_al}: `)
                    : (isSpanish ? 'Saldo: ' : 'Balance: ')}
                  <strong>{plata(cuenta.saldo_pcm, cuenta.moneda)}</strong>
                </Typography>
                {/* 25 sep 2026 (Augusto: "colocar la fecha de corte de
                    cada tarjeta en Conciliación y en Deuda"). Es lo que
                    define los períodos de esta tabla: verlo acá evita
                    preguntarse por qué un ciclo va del 19 al 18. */}
                {cuenta.tipo === 'tarjeta_credito' && (
                  <Typography
                    variant="caption"
                    color={cuenta.dia_cierre ? 'text.secondary' : 'warning.main'}
                    sx={{ display: 'block' }}
                  >
                    {cuenta.dia_cierre
                      ? (isSpanish
                        ? `Cierra el ${cuenta.dia_cierre} de cada mes`
                        : `Closes on the ${cuenta.dia_cierre} each month`)
                      : (isSpanish
                        ? 'Sin fecha de corte — los períodos no van a coincidir con tu estado de cuenta. Ponla en Deudas.'
                        : "No closing day — periods won't match your statement. Set it under Debts.")}
                  </Typography>
                )}
                {cuenta.saldo_desactualizado_dias > 7 && (
                  <Typography variant="caption" color="warning.main">
                    {isSpanish
                      ? `Sin datos nuevos hace ${cuenta.saldo_desactualizado_dias} días — este NO es tu saldo de hoy.`
                      : `No new data for ${cuenta.saldo_desactualizado_dias} days — this is NOT today's balance.`}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Señales de que algo pide atención, antes de que el
                    usuario tenga que buscarlas. */}
                {cuenta.pendientes_revision > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    label={isSpanish
                      ? `${cuenta.pendientes_revision} sin revisar`
                      : `${cuenta.pendientes_revision} unreviewed`}
                  />
                )}
                {ultima ? (
                  <Chip
                    size="small"
                    color={ultima.estado === 'pendiente' ? 'error' : 'success'}
                    variant="outlined"
                    label={isSpanish
                      ? `Conciliada el ${ultima.fecha}${ultima.diferencia ? ` · dif. ${plata(ultima.diferencia, cuenta.moneda)}` : ''}`
                      : `Reconciled ${ultima.fecha}${ultima.diferencia ? ` · diff. ${plata(ultima.diferencia, cuenta.moneda)}` : ''}`}
                  />
                ) : (
                  <Chip size="small" variant="outlined" label={isSpanish ? 'Nunca conciliada' : 'Never reconciled'} />
                )}
                <Button size="small" variant={estaAbierta ? 'text' : 'contained'} onClick={() => (estaAbierta ? setAbierta(null) : abrir(cuenta))}>
                  {estaAbierta
                    ? (isSpanish ? 'Cerrar' : 'Close')
                    : (isSpanish ? 'Conciliar' : 'Reconcile')}
                </Button>
              </Box>
            </Box>

            {estaAbierta && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                {/* 24 sep 2026 (Augusto: "la idea es que se utilice
                    balance final, que se convierte en el balance inicial
                    del siguiente mes, y el balance corriente").
                    La cadena es lo que hace que conciliar sirva: si un mes
                    cuadra, todo lo anterior cuadra, y la diferencia queda
                    acotada a un mes en vez de flotar sobre toda la
                    historia de la cuenta. Por eso conviene ir del mes más
                    viejo al más nuevo. */}
                {meses?.account_id === cuenta.account_id && meses.meses.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {meses.falta_dia_cierre && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {isSpanish
                          ? 'Esta tarjeta no tiene día de cierre, así que la estoy cortando a fin de mes — y eso NO es lo que dice tu estado de cuenta. Ponle el día de cierre en Cuentas y los períodos van a coincidir con los del banco.'
                          : "This card has no closing day, so I'm cutting at month end — which is NOT what your statement says. Set its closing day under Accounts and the periods will match the issuer's."}
                      </Alert>
                    )}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {meses.corte === 'fecha_corte'
                        ? (isSpanish
                          ? `Ciclo a ciclo · cierra el ${meses.dia_cierre} · ${meses.anio}`
                          : `Cycle by cycle · closes on the ${meses.dia_cierre} · ${meses.anio}`)
                        : (isSpanish ? `Mes a mes · ${meses.anio}` : `Month by month · ${meses.anio}`)}
                      <Button size="small" onClick={() => { const a2 = anio - 1; setAnio(a2); cargarMeses(cuenta.account_id, a2); }}>
                        {anio - 1}
                      </Button>
                      <Button size="small" onClick={() => { const a2 = anio + 1; setAnio(a2); cargarMeses(cuenta.account_id, a2); }}>
                        {anio + 1}
                      </Button>
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell>{isSpanish ? 'Período' : 'Period'}</TableCell>
                            <TableCell align="right">{isSpanish ? 'Saldo inicial' : 'Opening'}</TableCell>
                            <TableCell align="right">{isSpanish ? 'Entró' : 'In'}</TableCell>
                            <TableCell align="right">{isSpanish ? 'Salió' : 'Out'}</TableCell>
                            <TableCell align="right">{isSpanish ? 'Saldo final' : 'Closing'}</TableCell>
                            <TableCell align="right">{isSpanish ? 'Banco' : 'Bank'}</TableCell>
                            <TableCell align="center">{isSpanish ? 'Conciliar' : 'Reconcile'}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {meses.meses.map((m) => {
                            const c = m.conciliacion;
                            const cuadrado = c && Math.abs(c.diferencia) < 0.01;
                            return (
                              <TableRow key={`${m.anio}-${m.mes}`} hover>
                                {/* 24 sep 2026 (Augusto: "el balance de
                                    tarjetas de crédito se dan de acuerdo a
                                    fecha de corte, y las cuentas bancarias a
                                    fin de mes"). La etiqueta la arma el
                                    backend según el tipo de cuenta: un mes
                                    para el banco, el ciclo completo para la
                                    tarjeta. */}
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.etiqueta || `${String(m.mes).padStart(2, '0')}/${m.anio}`}</TableCell>
                                <TableCell align="right">{plata(m.saldo_inicio, cuenta.moneda)}</TableCell>
                                <TableCell align="right" sx={{ color: 'success.main' }}>
                                  {m.ingresos ? plata(m.ingresos, cuenta.moneda) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                  {m.gastos ? plata(m.gastos, cuenta.moneda) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                  {plata(m.saldo_fin, cuenta.moneda)}
                                </TableCell>
                                <TableCell align="right">
                                  {c ? (
                                    <Typography variant="body2" color={cuadrado ? 'success.main' : 'error.main'}>
                                      {plata(c.saldo_banco, cuenta.moneda)}
                                      {!cuadrado && (
                                        <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                                          {plata(c.diferencia, cuenta.moneda)}
                                        </Typography>
                                      )}
                                    </Typography>
                                  ) : '—'}
                                </TableCell>
                                <TableCell align="center">
                                  <Button
                                    size="small"
                                    onClick={() => { setFecha(m.hasta); setSaldoBanco(''); setResultado(null); }}
                                  >
                                    {isSpanish ? 'Este mes' : 'This month'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {isSpanish
                        ? `Balance corriente (hoy, ${meses.corriente?.fecha}): ${plata(meses.corriente?.saldo, cuenta.moneda)}. ${meses.corte === 'fecha_corte' ? 'Los períodos siguen la fecha de corte de la tarjeta, no el fin de mes: son los que aparecen en tu estado de cuenta. ' : ''}El saldo final de cada período es el inicial del siguiente — conviene empezar por el más antiguo: cada período que cuadra reduce el terreno donde puede estar el problema.`
                        : `Current balance (today, ${meses.corriente?.fecha}): ${plata(meses.corriente?.saldo, cuenta.moneda)}. Each month's closing balance is the next one's opening — start from the oldest month: every month that matches narrows down where the problem can be.`}
                    </Typography>
                  </Box>
                )}

                {/* 25 sep 2026 (Augusto: "dejar la opción para que el
                    usuario indique el saldo a hoy manualmente"). El atajo
                    para el caso más común: el último extracto es viejo,
                    no vas a conseguir los que faltan, y solo quieres que
                    el saldo de hoy sea el correcto para empezar. */}
                <Box sx={{ mb: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {isSpanish
                      ? '¿Solo quieres poner al día el saldo y empezar a registrar? Dime cuánto hay hoy:'
                      : 'Just want to bring the balance up to date and start recording? Tell me how much there is today:'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      type="number"
                      label={isSpanish
                        ? `Saldo hoy (${cuenta.moneda || 'PEN'})`
                        : `Balance today (${cuenta.moneda || 'PEN'})`}
                      value={saldoHoy[cuenta.account_id] ?? ''}
                      onChange={(e) => setSaldoHoy((p) => ({ ...p, [cuenta.account_id]: e.target.value }))}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={trabajando || (saldoHoy[cuenta.account_id] ?? '') === ''}
                      onClick={() => fijarSaldoHoy(cuenta)}
                    >
                      {isSpanish ? 'Poner al día' : 'Bring up to date'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {isSpanish
                      ? 'Queda registrado en el historial, con la diferencia. No inventa movimientos: los que falten siguen faltando y se siguen viendo en la cadena de arriba.'
                      : 'It is recorded in the history, with the difference. It does not invent transactions: whatever is missing stays missing and still shows in the chain above.'}
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {isSpanish
                    ? 'Elige un mes arriba (o pon una fecha) y copia el saldo que muestra tu banco a esa fecha.'
                    : 'Pick a month above (or set a date) and copy the balance your bank shows on that date.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                  <TextField
                    size="small"
                    type="date"
                    label={isSpanish ? 'Fecha del saldo' : 'Balance date'}
                    InputLabelProps={{ shrink: true }}
                    value={fecha}
                    onChange={(e) => { setFecha(e.target.value); setResultado(null); }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label={isSpanish
                      ? `Saldo en el banco (${cuenta.moneda || 'PEN'})`
                      : `Balance at the bank (${cuenta.moneda || 'PEN'})`}
                    value={saldoBanco}
                    onChange={(e) => { setSaldoBanco(e.target.value); setResultado(null); }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    disabled={saldoBanco === '' || trabajando}
                    onClick={() => comparar(cuenta)}
                  >
                    {isSpanish ? 'Comparar' : 'Compare'}
                  </Button>
                </Box>

                {resultado && resultado.account_id === cuenta.account_id && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    {resultado.cuadrada ? (
                      <Alert
                        severity="success"
                        icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                        action={
                          resultado.estado ? null : (
                            <Button color="inherit" size="small" disabled={trabajando} onClick={() => confirmar(cuenta, false)}>
                              {isSpanish ? 'Dejar registrado' : 'Record it'}
                            </Button>
                          )
                        }
                      >
                        {isSpanish
                          ? `Cuadra: al ${resultado.fecha} los dos dicen ${plata(resultado.saldo_banco, cuenta.moneda)}.`
                          : `It matches: on ${resultado.fecha} both say ${plata(resultado.saldo_banco, cuenta.moneda)}.`}
                      </Alert>
                    ) : (
                      <>
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                          {isSpanish
                            ? `Al ${resultado.fecha} el banco dice ${plata(resultado.saldo_banco, cuenta.moneda)} y PCM ${plata(resultado.saldo_pcm, cuenta.moneda)}. `
                            : `On ${resultado.fecha} the bank says ${plata(resultado.saldo_banco, cuenta.moneda)} and PCM ${plata(resultado.saldo_pcm, cuenta.moneda)}. `}
                          <strong>
                            {isSpanish
                              ? `Faltan ${plata(Math.abs(resultado.diferencia), cuenta.moneda)} ${resultado.diferencia > 0 ? 'por registrar' : 'de más'}.`
                              : `${plata(Math.abs(resultado.diferencia), cuenta.moneda)} ${resultado.diferencia > 0 ? 'missing' : 'in excess'}.`}
                          </strong>
                        </Alert>

                        {/* 25 sep 2026 (Augusto: "se necesita un apoyo
                            de IA"). El botón aparece con la diferencia a
                            la vista, que es el momento en que uno se
                            queda sin saber qué mirar. */}
                        <Box sx={{ mb: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={pidiendoAyuda}
                            onClick={() => pedirAyuda(cuenta)}
                          >
                            {pidiendoAyuda
                              ? (isSpanish ? 'Revisando...' : 'Checking...')
                              : (isSpanish ? 'Ayúdame a encontrar la diferencia' : 'Help me find the difference')}
                          </Button>
                        </Box>

                        {ayuda && (
                          <Alert
                            severity={ayuda.confianza === 'baja' ? 'info' : 'success'}
                            sx={{ mb: 2 }}
                          >
                            <Typography variant="body2" sx={{ mb: ayuda.accion ? 1 : 0 }}>
                              {ayuda.diagnostico}
                            </Typography>
                            {ayuda.accion && (
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {ayuda.accion}
                              </Typography>
                            )}
                            {/* Los hallazgos son del sistema, no de la IA:
                                coincidencias exactas, verificadas. Se
                                muestran aparte para que se distinga lo
                                comprobado de lo interpretado. */}
                            {ayuda.hallazgos?.length > 0 && (
                              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                                {ayuda.hallazgos.map((h, i) => (
                                  <li key={i}>
                                    <Typography variant="caption">{h.detalle}</Typography>
                                  </li>
                                ))}
                              </Box>
                            )}
                            {ayuda.confianza === 'baja' && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                {isSpanish
                                  ? 'El asistente no está seguro: tómalo como una pista, no como un diagnóstico.'
                                  : "The assistant isn't confident: treat this as a hint, not a diagnosis."}
                              </Typography>
                            )}
                          </Alert>
                        )}

                        {resultado.pistas?.length > 0 ? (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {isSpanish ? 'Qué puede estar pasando' : 'What might be going on'}
                            </Typography>
                            {resultado.pistas.map((p) => (
                              <Alert
                                key={p.tipo}
                                severity="info"
                                sx={{ mb: 1 }}
                                action={
                                  <Button color="inherit" size="small" onClick={() => navigate(p.ruta)}>
                                    {etiquetaPista(p)}
                                  </Button>
                                }
                              >
                                {textoPista(p, cuenta.moneda)}
                              </Alert>
                            ))}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {isSpanish
                              ? 'No encuentro nada obvio que lo explique. Lo más probable es que falte registrar algún movimiento suelto de esta cuenta.'
                              : "I can't find an obvious explanation. Most likely a transaction on this account is simply missing."}
                          </Typography>
                        )}

                        {/* Aceptar el saldo del banco NO inventa
                            movimientos: reconoce que el banco es la
                            fuente de verdad del saldo y que a PCM le
                            faltan registros. Se dice tal cual, para que
                            nadie crea que esto "arregla" el historial. */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={trabajando}
                            onClick={() => confirmar(cuenta, true)}
                          >
                            {isSpanish ? 'Usar el saldo del banco' : 'Use the bank balance'}
                          </Button>
                          <Button size="small" disabled={trabajando} onClick={() => confirmar(cuenta, false)}>
                            {isSpanish ? 'Anotar y resolver después' : 'Note it and fix later'}
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {isSpanish
                            ? 'Usar el saldo del banco corrige el punto de partida de esta cuenta para que el calendario y los saldos queden bien de acá en adelante. No inventa movimientos: los que falten siguen faltando, y las pistas de arriba siguen valiendo.'
                            : 'Using the bank balance fixes this account\'s starting point so balances and the calendar are right from here on. It does not invent transactions: whatever is missing stays missing, and the hints above still apply.'}
                        </Typography>
                      </>
                    )}
                  </>
                )}
              </Box>
            )}
          </Paper>
        );
      })}
    </Container>
  );
};

export default ConciliacionPage;
